import { useState, useEffect } from "react";
import { Box, Stack, Grid, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getColors } from "./bookings.styles";
import ReportLoader from "../../Reports/Loader";
import BookingMetrics from "./BookingMetrics";
import BookingAttendanceTrend from "./BookingAttendanceTrend";
import PeakBookingSlots from "./PeakBookingSlots";
import BookingHistory from "./BookingHistory";
import { getBookingsReportApi } from "../../../api/reports.api";
import dayjs from "dayjs";

const Bookings = ({ dateRange, onDateChange }) => {
  const theme = useTheme();
  const colors = getColors(theme);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filterParams, setFilterParams] = useState({});

  useEffect(() => {
    fetchBookingsData();
  }, [dateRange, filterParams]);

  const fetchBookingsData = async () => {
    setLoading(true);
    try {
      const params = {
        from: dateRange?.startDate ? dateRange.startDate.toISOString() : undefined,
        to: dateRange?.endDate ? dateRange.endDate.toISOString() : undefined,
        ...filterParams,
      };

      // Remove undefined values
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      const response = await getBookingsReportApi(params);

      // Process the API response and calculate metrics
      const items = response.data.data?.items || [];
      const processedData = processBookingsData(items, response.data.data);

      setData(processedData);
    } catch (error) {
      console.error("Error fetching bookings data:", error);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  // Process bookings data and calculate metrics
  const processBookingsData = (items, rawData) => {
    // Flatten all bookings
    const allBookings = [];
    items.forEach(lead => {
      if (lead.bookings && Array.isArray(lead.bookings)) {
        lead.bookings.forEach(booking => {
          allBookings.push({
            ...booking,
            leadId: lead._id,
            clientName: lead.client_name,
            contact: lead.contact,
            email: lead.email,
            services: lead.services,
            leadData: lead,
          });
        });
      }
    });

    // Calculate metrics
    const totalBookings = allBookings.length;
    const completedBookings = allBookings.filter(b => b.status?.toLowerCase() === "completed").length;
    const cancelledBookings = allBookings.filter(b => b.status?.toLowerCase() === "cancelled").length;
    const scheduledBookings = allBookings.filter(b => b.status?.toLowerCase() === "scheduled").length;
    const confirmedBookings = allBookings.filter(b => b.status?.toLowerCase() === "confirmed").length;
    const ongoingBookings = allBookings.filter(b => b.status?.toLowerCase() === "ongoing").length;

    // Show rate = (completed + ongoing) / (total - cancelled)
    const showRateDenominator = totalBookings - cancelledBookings;
    const showRate = showRateDenominator > 0
      ? Math.round(((completedBookings + ongoingBookings) / showRateDenominator) * 100)
      : 0;

    // Cancellation rate
    const cancellationRate = totalBookings > 0
      ? Math.round((cancelledBookings / totalBookings) * 100)
      : 0;

    // Calculate average lead to booking time (in days)
    const leadToBookingTimes = items
      .filter(lead => lead.bookings && lead.bookings.length > 0)
      .map(lead => {
        const firstBooking = lead.bookings.reduce((earliest, booking) => {
          return new Date(booking.date) < new Date(earliest.date) ? booking : earliest;
        }, lead.bookings[0]);
        return dayjs(firstBooking.date).diff(dayjs(lead.created_at), 'day');
      });

    const avgLeadToBooking = leadToBookingTimes.length > 0
      ? (leadToBookingTimes.reduce((sum, days) => sum + days, 0) / leadToBookingTimes.length).toFixed(1)
      : 0;

    // Generate daily booking trend data (by date)
    const dailyData = {};
    allBookings.forEach(booking => {
      if (!booking.date) return;
      const date = dayjs(booking.date).format('MMM DD');
      if (!dailyData[date]) {
        dailyData[date] = { bookings: 0, completed: 0, confirmed: 0, scheduled: 0, ongoing: 0, date };
      }
      dailyData[date].bookings++;
      const status = booking.status?.toLowerCase();
      if (status === 'completed') dailyData[date].completed++;
      if (status === 'confirmed') dailyData[date].confirmed++;
      if (status === 'scheduled') dailyData[date].scheduled++;
      if (status === 'ongoing') dailyData[date].ongoing++;
    });

    // Sort by date and convert to array
    const dailyTrend = Object.values(dailyData)
      .sort((a, b) => dayjs(a.date, 'MMM DD').unix() - dayjs(b.date, 'MMM DD').unix())
      .map(d => ({
        date: d.date,
        bookings: d.bookings,
        completed: d.completed,
        showRate: d.bookings > 0 ? Math.round((d.completed / d.bookings) * 100) : 0,
      }));

    // Generate attendance trend data (by month) - for backward compatibility
    const monthlyData = {};
    allBookings.forEach(booking => {
      if (!booking.date) return;
      const date = dayjs(booking.date);
      const month = date.format('MMM').toUpperCase();
      if (!monthlyData[month]) {
        monthlyData[month] = { bookings: 0, completed: 0, month };
      }
      monthlyData[month].bookings++;
      if (booking.status?.toLowerCase() === 'completed') {
        monthlyData[month].completed++;
      }
    });

    // Sort by month order
    const monthOrder = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
    const attendanceTrend = monthOrder
      .filter(month => monthlyData[month])
      .map(month => {
        const d = monthlyData[month];
        return {
          month: d.month,
          bookings: d.bookings,
          attendance: d.completed,
          showRate: d.bookings > 0 ? Math.round((d.completed / d.bookings) * 100) : 0,
        };
      });

    // Generate peak slots heatmap data
    const days = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
    const timeSlots = ["09:00", "12:00", "15:00", "18:00"];
    const peakSlots = {};

    timeSlots.forEach(time => {
      peakSlots[time] = {};
      days.forEach(day => {
        peakSlots[time][day] = 0;
      });
    });

    // Map slot strings to time slot keys
    const slotToTimeKey = {
      "09:00 – 10:00": "09:00",
      "10:00 – 11:00": "09:00",
      "11:00 – 12:00": "09:00",
      "12:00 – 13:00": "12:00",
      "13:00 – 14:00": "12:00",
      "14:00 – 15:00": "15:00",
      "15:00 – 16:00": "15:00",
      "16:00 – 17:00": "15:00",
      "17:00 – 18:00": "18:00",
      "18:00 – 19:00": "18:00",
      "19:00 – 20:00": "18:00",
    };

    allBookings.forEach(booking => {
      if (!booking.date || !booking.slot) return;
      const date = dayjs(booking.date);
      const dayName = days[date.day() === 0 ? 6 : date.day() - 1]; // Convert Sun=0 to index 6

      // Get time slot from slot string (e.g., "09:00 – 10:00")
      const slotKey = slotToTimeKey[booking.slot];
      if (slotKey && peakSlots[slotKey] && peakSlots[slotKey][dayName] !== undefined) {
        peakSlots[slotKey][dayName]++;
      }
    });

    return {
      items: items,
      total: rawData?.total || 0,
      metrics: {
        totalBookings,
        showRate,
        cancellations: cancellationRate,
        avgLeadToBooking: parseFloat(avgLeadToBooking),
      },
      dailyTrend,
      attendanceTrend,
      peakSlots,
    };
  };

  const handleFilter = (filters) => {
    // Convert frontend filter format to backend params
    const params = {};
    if (filters?.bookingStatuses) {
      const activeStatuses = Object.entries(filters.bookingStatuses)
        .filter(([_, isActive]) => isActive)
        .map(([key]) => key);
      if (activeStatuses.length > 0) {
        params.bookingStatus = activeStatuses[0]; // API expects single status
      }
    }
    if (filters?.consultant) {
      params.consultant = filters.consultant;
    }
    if (filters?.startDate && filters?.endDate) {
      params.from = filters.startDate;
      params.to = filters.endDate;
    }
    setFilterParams(params);
  };

  if (loading) {
    return <ReportLoader />;
  }

  // Process bookings data from API response
  const bookingsItems = data?.items || [];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {/* Metrics Cards */}
      <BookingMetrics data={data?.metrics} />

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ "& > .MuiGrid-root": { display: "flex" } }}>
        {/* Booking vs Attendance Trend Chart */}
        <Grid size={{ xs: 12, lg: 7 }} sx={{ display: "flex" }}>
          <BookingAttendanceTrend data={data?.dailyTrend} />
        </Grid>

        {/* Peak Booking Slots Heatmap */}
        <Grid size={{ xs: 12, lg: 5 }} sx={{ display: "flex" }}>
          <PeakBookingSlots data={data?.peakSlots} />
        </Grid>
      </Grid>

      {/* Booking History Table */}
      <BookingHistory
        data={bookingsItems}
        onFilter={handleFilter}
        totalCount={data?.total || 0}
      />
    </Box>
  );
};

export default Bookings;
